import * as Yup from "yup";
import {
  JaNej,
  LawyerOrLawFirmStatus,
  ParticipantType,
  WithdrawalReason,
} from "../model/registrationEnums";
import {
  canEnterCreationShareFields,
  INELIGIBLE_PARTICIPANT_MESSAGE,
  isEligibleLawFirmStatus,
  isEligibleParticipant,
  isEligiblePartnerExamStatus,
  isValidLawyerOwnerShare,
  isValidNonLawyerOwnerShare,
  requiresNonLawyerOwnerShare,
} from "./participantEligibilityRules";
import {
  cvrField,
  SELECT_REQUIRED_MESSAGE,
} from "../validation/registrationValidationShared";

const isLawyerOrLawFirmField = Yup.string()
  .required(SELECT_REQUIRED_MESSAGE)
  .oneOf(
    [
      LawyerOrLawFirmStatus.Ja,
      LawyerOrLawFirmStatus.IkkeAdvokatselskab,
      LawyerOrLawFirmStatus.IkkeAdvokat,
    ],
    SELECT_REQUIRED_MESSAGE,
  )
  .test("eligible-law-firm", INELIGIBLE_PARTICIPANT_MESSAGE, (value) =>
    isEligibleLawFirmStatus(value ?? ""),
  );

const passedPartnerExamField = Yup.string().when("isLawyerOrLawFirm", {
  is: LawyerOrLawFirmStatus.IkkeAdvokat,
  then: () =>
    Yup.string()
      .required(SELECT_REQUIRED_MESSAGE)
      .oneOf([JaNej.Ja, JaNej.Nej], SELECT_REQUIRED_MESSAGE)
      .test("eligible-partner-exam", INELIGIBLE_PARTICIPANT_MESSAGE, (value) =>
        isEligiblePartnerExamStatus(value ?? ""),
      ),
  otherwise: (schema) => schema.notRequired(),
});

const nonLawyerOwnerShareSchema = () =>
  Yup.string()
    .required("Ejerandel er påkrævet.")
    .matches(/^\d+([.,]\d+)?$/, "Angiv et tal.")
    .test(
      "max-non-lawyer-share",
      "Ejerandelen må højst være 9,99 %.",
      (value) => isValidNonLawyerOwnerShare(value ?? ""),
    );

const lawyerOwnerShareSchema = () =>
  Yup.string()
    .required("Ejerandel er påkrævet.")
    .matches(/^\d+([.,]\d+)?$/, "Angiv et tal.")
    .test(
      "lawyer-owner-share-range",
      "Angiv et tal større end 0 og højst 100.",
      (value) => isValidLawyerOwnerShare(value ?? ""),
    );

const voteShareSchema = () =>
  Yup.string()
    .required("Stemmeandel er påkrævet.")
    .matches(/^\d+([.,]\d+)?$/, "Angiv et tal.")
    .test(
      "vote-share-range",
      "Angiv et tal større end 0 og højst 100.",
      (value) => isValidLawyerOwnerShare(value ?? ""),
    );

const resolveShareFieldEligibility = (
  isLawyerOrLawFirm: unknown,
  passedPartnerExam: unknown,
): { status: string; exam: string } => ({
  status: String(isLawyerOrLawFirm ?? ""),
  exam: String(passedPartnerExam ?? ""),
});

const creationOwnerShareField = Yup.string().when(
  ["isLawyerOrLawFirm", "passedPartnerExam"],
  ([isLawyerOrLawFirm, passedPartnerExam], schema) => {
    const { status, exam } = resolveShareFieldEligibility(
      isLawyerOrLawFirm,
      passedPartnerExam,
    );

    if (requiresNonLawyerOwnerShare(status, exam)) {
      return nonLawyerOwnerShareSchema();
    }

    if (canEnterCreationShareFields(status, exam)) {
      return lawyerOwnerShareSchema();
    }

    return schema.notRequired();
  },
);

/** Stemmeandel — påkrævet når ejer- og stemmeandele vises. */
export const creationVoteShareField = Yup.string().when(
  ["isLawyerOrLawFirm", "passedPartnerExam"],
  ([isLawyerOrLawFirm, passedPartnerExam], schema) => {
    const { status, exam } = resolveShareFieldEligibility(
      isLawyerOrLawFirm,
      passedPartnerExam,
    );

    if (canEnterCreationShareFields(status, exam)) {
      return voteShareSchema();
    }

    return schema.notRequired();
  },
);

const participantEligibilityFields = {
  isLawyerOrLawFirm: isLawyerOrLawFirmField,
  passedPartnerExam: passedPartnerExamField,
};

const leavesOtherOnEntryField = Yup.string().when(
  ["isLawyerOrLawFirm", "passedPartnerExam"],
  ([isLawyerOrLawFirm, passedPartnerExam], schema) => {
    const { status, exam } = resolveShareFieldEligibility(
      isLawyerOrLawFirm,
      passedPartnerExam,
    );

    if (isEligibleParticipant(status, exam)) {
      return Yup.string()
        .required(SELECT_REQUIRED_MESSAGE)
        .oneOf([JaNej.Ja, JaNej.Nej], SELECT_REQUIRED_MESSAGE);
    }

    return schema.notRequired();
  },
);

const changeOtherCompanyNameField = Yup.string().when(
  ["isLawyerOrLawFirm", "leavesOtherOnEntry"],
  {
    is: (status: string, leaves: string) =>
      status === LawyerOrLawFirmStatus.Ja && leaves === JaNej.Ja,
    then: () => Yup.string().required("Navn på virksomhed er påkrævet."),
    otherwise: (schema) => schema.notRequired(),
  },
);

/** Felter for ansvarlige deltagere ved oprettelse af interessentskab. */
export const interessentskabParticipantEligibilityFields = {
  isLawyerOrLawFirm: Yup.string()
    .required(SELECT_REQUIRED_MESSAGE)
    .oneOf(
      [
        LawyerOrLawFirmStatus.Ja,
        LawyerOrLawFirmStatus.IkkeAdvokatselskab,
        LawyerOrLawFirmStatus.IkkeAdvokat,
      ],
      SELECT_REQUIRED_MESSAGE,
    )
    .test(
      "eligible-interessent-participant",
      INELIGIBLE_PARTICIPANT_MESSAGE,
      (value) => value === LawyerOrLawFirmStatus.Ja,
    ),
  passedPartnerExam: Yup.string().notRequired(),
  leavesOtherOnEntry: Yup.string().when("isLawyerOrLawFirm", {
    is: LawyerOrLawFirmStatus.Ja,
    then: () =>
      Yup.string()
        .required(SELECT_REQUIRED_MESSAGE)
        .oneOf([JaNej.Ja, JaNej.Nej], SELECT_REQUIRED_MESSAGE),
    otherwise: (schema) => schema.notRequired(),
  }),
};

/** Felter der valideres når en interessent indtræder ved ændring af interessentskab. */ export const changeEnteringPartyEligibilityFields =
  {
    ...interessentskabParticipantEligibilityFields,
    otherCompanyName: changeOtherCompanyNameField,
  };

/** Felter der valideres når en deltager udtræder ved ændring. */
export const changeLeavingPartyFields = {
  participantType: Yup.string()
    .required(SELECT_REQUIRED_MESSAGE)
    .oneOf(
      [ParticipantType.Person, ParticipantType.Company],
      SELECT_REQUIRED_MESSAGE,
    ),
  withdrawalReason: Yup.string().when("participantType", {
    is: ParticipantType.Person,
    then: () =>
      Yup.string()
        .required(SELECT_REQUIRED_MESSAGE)
        .oneOf(
          [WithdrawalReason.ContinuesAsLawyer, WithdrawalReason.Deposits],
          SELECT_REQUIRED_MESSAGE,
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  continuedCompanyName: Yup.string().when(
    ["participantType", "withdrawalReason"],
    {
      is: (type: string, reason: string) =>
        type === ParticipantType.Person &&
        reason === WithdrawalReason.ContinuesAsLawyer,
      then: () => Yup.string().required("Virksomhed er påkrævet."),
      otherwise: (schema) => schema.notRequired(),
    },
  ),
  continuedCompanyCvr: Yup.string().when(
    ["participantType", "withdrawalReason"],
    {
      is: (type: string, reason: string) =>
        type === ParticipantType.Person &&
        reason === WithdrawalReason.ContinuesAsLawyer,
      then: () => cvrField,
      otherwise: (schema) => schema.notRequired(),
    },
  ),
};

/** Felter der valideres når en selskabsdeltager indtræder ved ændring af advokatselskab. */
export const changeAdvokatselskabEnteringPartyEligibilityFields = {
  ...participantEligibilityFields,
  ownerShare: creationOwnerShareField,
  voteShare: creationVoteShareField,
  leavesOtherOnEntry: leavesOtherOnEntryField,
  otherCompanyName: changeOtherCompanyNameField,
};

/** Felter for deltager-eligibility ved oprettelse (stemmeandel håndteres separat). */
export const creationParticipantEligibilityFields = {
  ...participantEligibilityFields,
  ownerShare: creationOwnerShareField,
  leavesOtherOnEntry: leavesOtherOnEntryField,
};
